import { CurrencyPipe, DatePipe } from '@angular/common';
import { ChangeDetectorRef, Component, EventEmitter, Input, Output, QueryList, ViewChildren, ChangeDetectionStrategy } from '@angular/core';
import { MatSort, SortDirection } from '@angular/material/sort';
import { AbstractPaginatedTableComponent } from '../../../components/paginated-table/paginated-table.component';
import { Operation, OperationSortInput } from '../../../models/generated/graphql';
import { MAJHeader, OperationDashboardColumn } from '../operations-dashboard-columns';
import { getTagAppearance, TagAppearance } from '../../../utils/tag-appearance';

import { AuthService } from '../../../services/auth-service.service';

export enum OperationSelectionActionType {
  CreateBordereau,
  Dispatch,
  EmailConsultant,
  Terminate,
  refuse,
  cancel,
}
export type OperationSelectionAction = {
  type: OperationSelectionActionType;
  operationIds: number[];
};
export type OperationSelectionActionConfig = {
  type: OperationSelectionActionType;
  label: string;
  icon: string;
};

@Component({
    selector: 'app-operations-table',
    templateUrl: './operations-table.component.html',
    styleUrls: ['./operations-table.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class OperationsTableComponent extends AbstractPaginatedTableComponent<Operation> {
  @ViewChildren('operationsSort') operationsSort!: QueryList<MatSort>;

  isOperationSelected(operationId: number): boolean {
    // return this.selection.isSelected(operationId)
    return this.selection.includes(operationId);
  }
  selectOperation(operationId: number, state: boolean) {
    // this.selection.toggle(operationId);
    if (this.selection.includes(operationId)) {
      this.selection.splice(this.selection.indexOf(operationId), 1);
    } else {
      this.selection.push(operationId);
    }
  }
  @Input() dashboardKey: string;

  @Input()
  columnSelection: OperationDashboardColumn[] = [];

  // display paginator
  @Input() withPaginator: boolean = true;

  // display CSV export
  @Input() withCSVExport: boolean = false;

  // display CSV export
  @Input() withRefresh: boolean = true;

  // display number of operations
  @Input() withTotalNumberDisplayed: boolean = true;

  // display if true will fetch data on init
  @Input() fetchOnInit: boolean = true;

  // display checkboxes
  @Input() withSelects: boolean = false;

  // are rows clickable
  @Input() withClickableRow: boolean = true;

  @Input()
  selectionActions: OperationSelectionActionConfig[] = [];

  @Output()
  onOperationRowClick = new EventEmitter<Operation>();

  @Output()
  onSelectionActionClicked = new EventEmitter<OperationSelectionAction>();
  //onSelectionActionClicked = new EventEmitter<Operation[]>();

  @Input() allowedActions: OperationSelectionActionType[] | null = null;

  @Input() showCheckbox: (operation: Operation) => boolean = () => true;

  @Output()
  onDeleteDraftClicked = new EventEmitter<Operation>();

  @Output()
  onSortCriteriaUpdate = new EventEmitter<OperationSortInput | undefined>();

  @Output()
  onExportClick = new EventEmitter();

  @Output()
  onRefreshRequested = new EventEmitter<void>();

  selection: number[] = [];

  displayedColumns: string[];

  isBackOffice: boolean;

  sortActive?: string;
  sortDirection?: SortDirection;

  constructor(
    private currencyPipe: CurrencyPipe,
    private datePipe: DatePipe,
    authService: AuthService,
    private cdref: ChangeDetectorRef
  ) {
    super();
    this.isBackOffice = authService.isBackOffice();
  }

  ngOnInit() {
    this.displayedColumns = this.withSelects ? ['select'] : [];
    this.displayedColumns.push(...this.columnSelection.map(column => column.header));
    if (this.fetchOnInit) {
      this.fetchData(true);
    }

    if (this.displayedColumns.includes(MAJHeader)) {
      this.sortActive = MAJHeader;

      this.sortDirection = 'desc';
      if (this.dashboardKey === 'control-a-effectuer' || this.dashboardKey === 'a-affecter') {
        //set asc by default for MAJ column as it is the most logical default sort for this column
        this.sortDirection = 'asc';
        this.updateSortCriteria({ active: MAJHeader, direction: 'asc' } as MatSort);
      }
    }
  }

  private updateSortCriteria(sort: MatSort) {
    let querySortCriteria = this.wrapSortCriteria(sort);
    this.onSortCriteriaUpdate.emit(querySortCriteria);
  }
  private wrapSortCriteria(sort: MatSort): OperationSortInput | undefined {
    let column = this.columnSelection.find(column => column.header === sort.active);
    if (!column || sort.direction == '') {
      return undefined;
    }
    let sortCriteria = column?.getSortCriteria(sort.direction === 'asc');
    return sortCriteria;
  }

  override ngAfterViewInit() {
    super.ngAfterViewInit();

    this.operationsSort.changes.subscribe(_ => {
      if (this.operationsSort.length > 0) {
        let sort = this.operationsSort.first;
        if (sort) {
          if (this.sortActive && this.sortDirection) {
            sort.sort({ id: this.sortActive, start: this.sortDirection, disableClear: true });
            this.cdref.detectChanges();
          }
          sort.sortChange.subscribe(() => {
            this.sortActive = sort.active;
            this.sortDirection = sort.direction;
            this.updateSortCriteria(sort);
          });
        }
      }
    });
  }

  onRowClick(operation: Operation) {
    if (this.withClickableRow) {
      this.onOperationRowClick.emit(operation);
    }
  }

  displayColumnInfo(column: OperationDashboardColumn, operation: Operation) {
    return column.getDataFromOperation(operation, this.currencyPipe, this.datePipe);
  }

  /** Whether the number of selected elements matches the total number of rows. */
  isAllSelected() {
    const numSelected = this.selection.length;
    const numRows = this.dataSource.data.length;
    return numSelected === numRows;
  }

  hasSelectableRows(): boolean {
    return this.dataSource?.filteredData?.some(op => this.showCheckbox(op));
  }

  /**
   * Toggle all the visible rows that have a checkbox.
   * If all visible rows are already selected, deselect all of them.
   * Otherwise, select all of them.
   * The selection is updated by adding/removing the IDs of the visible rows.
   * allVisibleRows is the filtered list of all rows that are visible and have a checkbox.
   */
  toggleAllRows() {
    const allVisibleRows = this.dataSource?.filteredData?.filter(op => this.showCheckbox(op)) || [];
    const allSelected = allVisibleRows.every(op => this.selection.includes(op.id));

    if (allSelected) {
      this.selection = this.selection.filter(id => !allVisibleRows.some(op => op.id === id));
    } else {
      const visibleRowIds = allVisibleRows.map(op => op.id);
      this.selection = [...new Set([...this.selection, ...visibleRowIds])];
    }
  }

  getSelected(): Operation[] {
    const selectedIds = this.selection;
    var selectedOperations = this.dataSource.data.filter(operation => selectedIds.includes(operation.id));
    return selectedOperations;
  }

  unselectAll() {
    this.selection = [];
  }

  onSelectionActionClick(type: OperationSelectionActionType) {
    if (this.selection.length === 0) return;
    const operations = this.getSelected();
    this.onSelectionActionClicked.emit({ type, operationIds: this.selection });
    // this.onSelectionActionClicked.emit(operations);
  }

  onDeleteDraftClick(operation: Operation) {
    this.onDeleteDraftClicked.emit(operation);
  }

  exportData() {
    this.onExportClick.emit();
  }

  refreshData() {
    this.onRefreshRequested.emit();
    this.fetchData(true);
  }

  getAppearance(tag: { code: string; isPermanent: boolean }) {
    return getTagAppearance(tag);
  }
}
